import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { UserPlus, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { addressSearch } from "@/lib/noteClaim";

const FIELDS = [
  { k: "first_name", label: "First name", req: true, w: 1 },
  { k: "last_name", label: "Last name", req: true, w: 1 },
  { k: "middle_name", label: "Middle name", w: 1 },
  { k: "dob", label: "Date of birth", req: true, type: "date", w: 1 },
  { k: "gender", label: "Gender", type: "gender", w: 1 },
  { k: "phone", label: "Phone", w: 1 },
  { k: "email", label: "Email", w: 2 },
  { k: "street", label: "Street", w: 2 },
  { k: "city", label: "City", w: 1 },
  { k: "state", label: "State", w: 1 },
  { k: "zip", label: "ZIP", w: 1 },
  { k: "payer_name", label: "Payer name", w: 1 },
  { k: "member_id", label: "Member ID", w: 1 },
  { k: "group_number", label: "Group number", w: 1 },
];

const US_STATES = "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA PR RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");
const ADDR_STATE_KEY = "revq.addrState";
const empty = (st = "") => ({ ...FIELDS.reduce((a, f) => ({ ...a, [f.k]: "" }), {}), state: st });

export default function AddRosterPatient({ clientId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [searchState, setSearchState] = useState(() => sessionStorage.getItem(ADDR_STATE_KEY) || "");
  const [form, setForm] = useState(() => empty(sessionStorage.getItem(ADDR_STATE_KEY) || ""));
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState(null); // array of {mrn,name,dob}
  const [sugg, setSugg] = useState([]); const [showSugg, setShowSugg] = useState(false);
  const addrTimer = useRef(null); const addrBox = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (addrBox.current && !addrBox.current.contains(e.target)) setShowSugg(false); };
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  // §1 address type-ahead — scoped to the picked state (required), 3+ chars, debounced.
  const runSearch = (v, st) => {
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (!st || String(v).trim().length < 3) { setSugg([]); setShowSugg(false); return; }
    addrTimer.current = setTimeout(async () => {
      try { const list = await addressSearch(String(v).trim(), st); setSugg(list); setShowSugg(list.length > 0); } catch { setSugg([]); }
    }, 300);
  };
  const onStreet = (v) => { set("street", v); runSearch(v, searchState); };
  const onSearchState = (st) => {
    setSearchState(st); sessionStorage.setItem(ADDR_STATE_KEY, st);
    setForm((f) => ({ ...f, state: st }));
    runSearch(form.street, st);
  };
  const pickAddr = (s) => { setForm((f) => ({ ...f, street: s.street_line || f.street, city: s.city || f.city, state: s.state || f.state, zip: s.zipcode || f.zip })); setShowSugg(false); };

  const reset = () => { setForm(empty(searchState)); setDupes(null); setBusy(false); setSugg([]); setShowSugg(false); };
  const close = () => { setOpen(false); reset(); };
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (dupes) setDupes(null); };

  const canSubmit = form.first_name.trim() && form.last_name.trim() && form.dob;

  const submit = async () => {
    if (!canSubmit || !clientId) return;
    setBusy(true); setDupes(null);
    const body = {};
    FIELDS.forEach((f) => { const v = String(form[f.k] || "").trim(); if (v) body[f.k] = v; });
    try {
      const { data } = await api.post(`/api/edi/note-to-claim/clients/${clientId}/roster-patients`, body);
      const p = data?.patient || {};
      toast.success("Patient added to roster", { description: `${p.full_name || `${body.first_name} ${body.last_name}`}${p.mrn ? ` · MRN ${p.mrn}` : ""}` });
      if (typeof onAdded === "function") onAdded(p);
      close();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        const list = Array.isArray(detail?.duplicates) ? detail.duplicates : [];
        setDupes(list);
        toast.warning("Possible duplicate patient", { description: detail?.message || "A patient with this name/DOB already exists." });
      } else if (status === 404) {
        toast.error("Unknown client — pick a client first.");
      } else {
        toast.error("Couldn't add patient", { description: typeof detail === "string" ? detail : "Check the name and date of birth." });
      }
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" data-testid="add-roster-patient-btn" onClick={() => setOpen(true)} disabled={!clientId}
        className="inline-flex items-center gap-1.5 h-9 px-[14px] rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40 whitespace-nowrap"
        style={{ background: "var(--ink, #141412)" }}>
        <UserPlus size={15} /> Add patient to roster
      </button>

      {open ? createPortal(
        <div className="fixed inset-0 z-[9998] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(20,20,18,.34)" }} onMouseDown={close} data-testid="add-roster-modal">
          <div className="w-[560px] max-w-full my-8 rounded-[16px] overflow-hidden" style={{ background: "#fff", border: "1px solid var(--hair,#E6E6E4)", boxShadow: "0 24px 60px -20px rgba(20,20,18,.4)" }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-5 h-[54px]" style={{ borderBottom: "1px solid var(--hair,#E6E6E4)" }}>
              <UserPlus size={17} className="text-[var(--gray2)]" />
              <div className="text-[15px] font-bold text-[var(--ink)]">Add patient to roster</div>
              <span className="flex-1" />
              <button type="button" onClick={close} data-testid="add-roster-close" className="w-8 h-8 rounded-[8px] flex items-center justify-center hover:bg-black/[.05]"><X size={16} className="text-[var(--gray3)]" /></button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.k} className={f.w === 2 ? "col-span-2" : "col-span-1"}>
                    <label className="block text-[11px] font-semibold uppercase tracking-[.04em] mb-1" style={{ color: "var(--gray3,#9B9B97)" }}>
                      {f.label}{f.req ? <span style={{ color: "#C0392B" }}> *</span> : null}
                    </label>
                    {f.type === "gender" ? (
                      <select data-testid={`add-roster-${f.k}`} value={form[f.k]} onChange={(e) => set(f.k, e.target.value)}
                        className="w-full h-[38px] px-3 rounded-[9px] text-[13.5px] outline-none bg-white" style={{ border: "1px solid var(--hair,#E6E6E4)" }}>
                        <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option><option value="unknown">Unknown</option>
                      </select>
                    ) : f.k === "street" ? (
                      <div className="relative" ref={addrBox}>
                        <div className="flex h-[38px] rounded-[9px] overflow-hidden focus-within:border-[#141412]" style={{ border: "1px solid var(--hair,#E6E6E4)" }}>
                          <label className="flex items-center gap-1 pl-2.5 pr-1 flex-shrink-0 text-[11.5px] font-semibold" style={{ background: "#F7F7F5", borderRight: "1px solid var(--hair,#E6E6E4)", color: "var(--gray2,#6E6E6B)" }}>
                            Search in
                            <select data-testid="add-roster-search-state" value={searchState} onChange={(e) => onSearchState(e.target.value)}
                              className="h-full bg-transparent outline-none text-[13px] font-bold cursor-pointer" style={{ color: searchState ? "var(--ink,#141412)" : "#C0392B" }}>
                              <option value="">State</option>
                              {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                            </select>
                          </label>
                          <input data-testid="add-roster-street" type="text" autoComplete="off" value={form.street} onChange={(e) => onStreet(e.target.value)} onFocus={() => setShowSugg(sugg.length > 0)}
                            placeholder={searchState ? `Start typing an address in ${searchState}…` : "Pick a state to search addresses"}
                            className="flex-1 min-w-0 h-full px-3 text-[13.5px] outline-none" />
                        </div>
                        {!searchState ? <div className="text-[11px] mt-1" style={{ color: "var(--gray3,#9B9B97)" }} data-testid="add-roster-state-hint">Pick a state first — address search only runs within one state. You can still type the street manually.</div> : null}
                        {showSugg ? (
                          <div className="absolute left-0 right-0 z-10 mt-1 rounded-[10px] overflow-hidden bg-white" style={{ border: "1px solid var(--hair,#E6E6E4)", boxShadow: "0 12px 30px -12px rgba(20,20,18,.35)" }} data-testid="add-roster-addr-suggestions">
                            {sugg.slice(0, 6).map((s, i) => (
                              <button key={i} type="button" onClick={() => pickAddr(s)} data-testid={`add-roster-addr-opt-${i}`}
                                className="block w-full text-left px-3 py-2 text-[12.5px] hover:bg-black/[.04]" style={{ color: "var(--ink)", borderBottom: i < Math.min(sugg.length, 6) - 1 ? "1px solid var(--hair,#E6E6E4)" : "none" }}>
                                {s.full_address || `${s.street_line}, ${s.city}, ${s.state} ${s.zipcode}`}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <input data-testid={`add-roster-${f.k}`} type={f.type === "date" ? "date" : "text"} value={form[f.k]} onChange={(e) => set(f.k, e.target.value)}
                        className="w-full h-[38px] px-3 rounded-[9px] text-[13.5px] outline-none" style={{ border: "1px solid var(--hair,#E6E6E4)" }} />
                    )}
                  </div>
                ))}
              </div>

              {dupes ? (
                <div className="mt-4 rounded-[10px] p-3" style={{ background: "#FFF6E8", border: "1px solid #F3D9A6" }} data-testid="add-roster-dupes">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-bold" style={{ color: "#9A6400" }}><AlertTriangle size={14} /> Possible duplicate{dupes.length === 1 ? "" : "s"} already on the roster</div>
                  <div className="mt-2 space-y-1">
                    {dupes.length === 0 ? <div className="text-[12.5px]" style={{ color: "#9A6400" }}>A matching patient already exists.</div>
                      : dupes.map((d, i) => (
                        <div key={d.mrn || i} className="text-[12.5px] flex items-center gap-2" style={{ color: "var(--ink)" }}>
                          <span className="font-semibold">{d.name || "—"}</span>
                          <span style={{ color: "var(--gray2)" }}>{d.dob || ""}</span>
                          {d.mrn ? <span className="text-[11px] px-1.5 py-[1px] rounded-[5px]" style={{ background: "#F1F1F4", color: "var(--gray2)" }}>MRN {d.mrn}</span> : null}
                        </div>
                      ))}
                  </div>
                  <div className="text-[11.5px] mt-2" style={{ color: "#9A6400" }}>If this is a different person, adjust the name or date of birth. Otherwise cancel and search for the existing patient.</div>
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-2 px-5 py-3.5" style={{ borderTop: "1px solid var(--hair,#E6E6E4)" }}>
              <span className="text-[11.5px]" style={{ color: "var(--gray3)" }}>An MRN is assigned automatically.</span>
              <span className="flex-1" />
              <button type="button" onClick={close} data-testid="add-roster-cancel" className="h-9 px-3.5 rounded-[9px] text-[13px] font-semibold" style={{ border: "1px solid var(--hair,#E6E6E4)", color: "var(--ink)" }}>Cancel</button>
              <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="add-roster-submit"
                className="h-9 px-4 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: "var(--ink,#141412)" }}>
                {busy ? "Adding…" : "Add patient"}
              </button>
            </div>
          </div>
        </div>, document.body) : null}
    </>
  );
}
